// frontend/src/StagingBanner.jsx
//
// Visual marker shown when the app is loaded on a non-production environment.
// Detection is hostname-based: if the URL contains "staging", we treat it as
// the staging environment. This is intentionally simple — if you ever add
// more non-prod environments (e.g. dev.ignitorads.co), update the IS_STAGING
// check below to match.
//
// Why hostname-based and not env-var-based: keeps the check at runtime (no
// build-time coupling), works whether the app is built locally or on Vercel,
// and doesn't require setting any new env vars on Vercel.
//
// Why visible: humans miss subtle UI differences when distracted. The banner
// is loud on purpose. Don't soften the colors — that defeats the point.

import React, { useEffect } from 'react';

// True when the current page is being served from a staging hostname.
// Returns false during server-side rendering or in unit tests.
const IS_STAGING =
  typeof window !== 'undefined' &&
  window.location &&
  window.location.hostname.includes('staging');

export default function StagingBanner() {
  // Patch the document title + favicon as a secondary signal — catches the
  // "scanning my browser tabs" case where the user might not see the banner.
  useEffect(() => {
    if (!IS_STAGING) return;

    // Title: prepend [STAGING] if not already there.
    const originalTitle = document.title;
    if (!originalTitle.startsWith('[STAGING]')) {
      document.title = `[STAGING] ${originalTitle}`;
    }

    // Favicon: replace with a red-dot version (rendered inline as data URL so
    // we don't have to ship a separate file). The dot is intentionally simple
    // and high-contrast.
    const favicon = document.querySelector("link[rel*='icon']");
    const originalHref = favicon?.href;
    if (favicon) {
      // 32x32 red circle on transparent background, encoded as data URL.
      favicon.href =
        'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">' +
        '<circle cx="16" cy="16" r="14" fill="%23ef4444"/>' +
        '<text x="16" y="22" font-family="Arial,sans-serif" font-size="14" font-weight="700" ' +
        'text-anchor="middle" fill="white">S</text></svg>';
    }

    // Cleanup if the component ever unmounts (defensive — in practice this
    // mounts at app root and never unmounts, but good React hygiene).
    return () => {
      document.title = originalTitle;
      if (favicon && originalHref) favicon.href = originalHref;
    };
  }, []);

  if (!IS_STAGING) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        // Note: NOT position: fixed. We let the banner take up its own space
        // and push everything below it down, rather than overlapping. This
        // avoids hiding the top edge of `page-header` (in the dashboard) or
        // the top of marketing pages. Costs ~28px of vertical real estate.
        position: 'relative',
        zIndex: 9999,
        background:
          'repeating-linear-gradient(45deg, #b91c1c, #b91c1c 12px, #991b1b 12px, #991b1b 24px)',
        color: '#fff',
        padding: '6px 14px',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
        userSelect: 'none',
      }}
    >
      ⚠ Staging Environment — not production. Data here is for testing only.
    </div>
  );
}

// Also export the flag so other code can check it (e.g. to disable analytics
// on staging, show a different favicon, etc.).
export { IS_STAGING };
